/* 216 §6 [RULED 75] — THE FOLDER BROWSER, EXTRACTED AS A CURE (nothing changed): the e-mail attachments window's
   breadcrumb, its subfolders and « Nouveau dossier » (206E addendum §A7), with the reading of the folders behind
   them, moved out of AttachmentSaveModal.tsx so the window a dropped document is filed in can be the same one. The
   markup, the state and the effect are the modal's own, character for character but for the two seams a move needs:
   `base` (the listing route the modal passed as `${urlBase}/attachment-folders`) and the modal's `open` guard, which
   the mount replaces. Module scope, its own hooks (Bug A's rule). */
import React from "react";
import { useState, useEffect } from "react";
import type { Dispatch } from "react";
import { Folder, ChevronRight } from "lucide-react";
import { api } from "../api";
import { RAD, FS } from "../lib/tokens";
import { AddButton, ListNotLoaded } from "./SharedUI";
import { useReadMark, usePageListContext } from "../hooks/usePageList"; // 212 §22
import { ListLoadContext, PAGE_LISTS } from "../lib/listLoad";
import type { Translate } from "../lib/interestRefusal";
/* 206E addendum §A7 — the name a new folder is created with, by the rule the
   server creates it with (`createSubfoldersUnder` cleans with this very
   function): shown before anything is sent, 181 A.4's rule for file names. */
import { sanitizeFolderName } from "../lib/folderName.mjs";

/* ── 206E addendum §A7 [RULED — Laurent: "would it be possible to offer to
   create a new folder in the chosen parent folder in sharepoint"] — A NEW
   FOLDER, WHERE THE ATTACHMENTS GO. Beside the breadcrumb, « + New folder »
   (the `AddButton`, which owns the « + »): a name field, confirmed with Enter
   or its button. The folder is created INSIDE the folder the browser shows
   (`POST {urlBase}/attachment-folders`, the listing's twin), and the browser
   then OPENS it, so the attachments save there. The field shows the name that
   will be created — through `sanitizeFolderName`, before anything is sent —
   and a name that cleans to nothing cannot be sent. A name that already exists
   opens that folder and says so. Shown only to those who hold
   `caseView.documents.edit`, the route's gate. At module scope (Bug A's rule):
   its own state, its own hooks. */
interface NewFolderFieldProps {
  t: Translate;
  /** the listing route's base: `${urlBase}/attachment-folders` (the e-mail window's), or the drop's */
  base: string;
  /** the folder the browser shows, as segments under the matter's root */
  path: readonly string[];
  /** [the folder's name as SharePoint holds it, whether it was already there] */
  onDone: Dispatch<[string, boolean]>;
  /** 216 §6 — the root the folder is created under (`matter`, or `client:<id>`) */
  root?: string;
}
interface FolderNote {
  text: string;
  bad: boolean;
}
function NewFolderField({ t, base, path, onDone, root = MATTER_ROOT }: Readonly<NewFolderFieldProps>) {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<FolderNote | null>(null);
  const clean = sanitizeFolderName(raw);
  const create = async () => {
    if (!clean || busy) return;
    setBusy(true);
    setNote(null);
    try {
      const out = await api<{ name?: string; existed?: boolean } | null>(base, {
        method: "POST",
        body: JSON.stringify({ path: path.join("/"), name: raw, ...(root !== MATTER_ROOT ? { root } : {}) }),
      });
      const name = out?.name ?? clean;
      const existed = out?.existed === true;
      setNote({
        text: existed
          ? t("attSave.new_folder_existed", { name, fallback: `« ${name} » already existed — opened` })
          : t("attSave.new_folder_created", { name, fallback: `Folder « ${name} » created and opened` }),
        bad: false,
      });
      setRaw("");
      setOpen(false);
      onDone([name, existed]);
    } catch (e) {
      const err = e as { code?: string; message?: string };
      setNote({
        text: err.code === "m365.not_connected"
          ? t("attSave.m365_not_connected", { fallback: "Microsoft 365 not connected — connect in Settings." })
          : err.message ?? t("attSave.new_folder_failed", { fallback: "The folder could not be created." }),
        bad: true,
      });
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6 }}>
        {note && <span style={{ fontSize: FS.s10_5, color: note.bad ? "var(--rd)" : "var(--gn)" }}>{note.text}</span>}
        <AddButton
          label={t("attSave.new_folder", { fallback: "New folder" })}
          title={t("attSave.new_folder_title", { fallback: "Create a folder inside the one shown, and open it" })}
          onClick={() => {
            setNote(null);
            setOpen(true);
          }}
        />
      </span>
    );
  }
  return (
    <div style={{ flexBasis: "100%", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginTop: 6 }}>
      <input autoComplete="off" /* 207 §18 */
        className="field-input no-contact-autofill"
        type="text"
        value={raw}
        placeholder={t("attSave.new_folder_placeholder", { fallback: "Folder name" })}
        aria-label={t("attSave.new_folder_placeholder", { fallback: "Folder name" })}
        onChange={(e) => {
          setRaw(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") void create();
        }}
        style={{ flex: "1 1 180px", fontSize: FS.s11, padding: "3px 6px" }}
      />
      <button
        type="button"
        className="btn btn-primary"
        aria-disabled={!clean || busy}
        onClick={() => {
          void create();
        }}
        style={{ padding: "2px 9px", fontSize: FS.s10_5, opacity: !clean || busy ? 0.5 : 1, cursor: !clean || busy ? "default" : "pointer" }}
      >
        {t("attSave.new_folder_create", { fallback: "Create" })}
      </button>
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() => {
          setOpen(false);
          setRaw("");
        }}
        style={{ padding: "2px 9px", fontSize: FS.s10_5 }}
      >
        {t("common.cancel", { fallback: "Cancel" })}
      </button>
      <span style={{ flexBasis: "100%", fontSize: FS.s10_5, color: clean ? "var(--tm)" : "var(--am)" }}>
        {clean
          ? t("attSave.new_folder_preview", { name: clean, fallback: `Will be created as « ${clean} »` })
          : raw.trim() !== "" && t("attSave.new_folder_empty", { fallback: "This name holds no usable character" })}
      </span>
      {note && <span style={{ flexBasis: "100%", fontSize: FS.s10_5, color: note.bad ? "var(--rd)" : "var(--gn)" }}>{note.text}</span>}
    </div>
  );
}

/* ── 216 §6 [RULED 75] — THE DESTINATIONS AT THE TOP: « Dossier de l'affaire », then each of the matter's clients
   whose folder the viewer may see (the server's list, `GET …/files/roots` — the Documents section's rule, applied
   there), a client with no folder listed and said so; in a collective case the clients come with a search [ADVISOR].
   The root chosen rides on every read and write (`root=` on the listing, `root` in the creation), and the server
   enforces it on each (lib/fileRoots.js). Inside either root the viewer browses and creates a folder as before. */
export interface FileRoots {
  matter: { hasFolder: boolean };
  clients: { id: number; name: string; hasFolder: boolean }[];
}
export const MATTER_ROOT = "matter";
export const clientRoot = (id: number): string => `client:${String(id)}`;
const fold = (s: string): string => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

interface FolderBrowserProps {
  t: Translate;
  /** the listing route's base (GET `?path=` lists, POST creates): the e-mail window's `${urlBase}/attachment-folders` */
  base: string;
  /** the folder shown, as segments under the root ([] = the root) */
  path: readonly string[];
  onPath: Dispatch<string[]>;
  /** whether « Nouveau dossier » is offered (the viewer holds `caseView.documents.edit` on the matter) */
  canCreateFolder: boolean;
  /** 216 §6 — the destinations the server lists for this viewer; absent, the matter's folder alone (as before) */
  roots?: FileRoots | null;
  /** the root chosen: `matter`, or `client:<id>` */
  root?: string;
  onRoot?: Dispatch<string>;
  /** a collective case: the clients come with a search */
  searchClients?: boolean;
}

export default function FolderBrowser({ t, base, path, onPath, canCreateFolder, roots = null, root = MATTER_ROOT, onRoot, searchClients = false }: Readonly<FolderBrowserProps>) {
  const setPath = onPath;
  const rootQ = root !== MATTER_ROOT ? `&root=${encodeURIComponent(root)}` : "";
  const [clientQ, setClientQ] = useState("");
  const clients = roots?.clients ?? [];
  const shownClients = searchClients && clientQ.trim() !== "" ? clients.filter((c) => fold(c.name).includes(fold(clientQ.trim()))) : clients;
  const rootName = root === MATTER_ROOT ? t("attSave.crumb_root", { fallback: "Case root" }) : (clients.find((c) => clientRoot(c.id) === root)?.name ?? t("fileWhere.client_folder", { fallback: "Client folder" }));
  const clientColor = (c: { id: number; hasFolder: boolean }): string => {
    if (root === clientRoot(c.id)) return "var(--ac)";
    return c.hasFolder ? "var(--tp)" : "var(--tm)";
  };
  const chooseRoot = (r: string) => {
    if (r === root || !onRoot) return;
    onRoot(r);
    setPath([]);
  };
  /* The folders read for ONE path, keyed by it: a path not yet answered is « loading » by derivation (no state
     is set inside the effect — react-hooks/set-state-in-effect), and an answer for an older path is never shown. */
  const [read, setRead] = useState<{ key: string; folders: { name: string }[] }>({ key: "\u0000", folders: [] });
  const pathStr = path.join("/");
  const pathKey = `${root}\u0000${pathStr}`; // the folders read for ONE root and path
  // Fetch the child folders for the current path whenever it changes.
  /* 212 §22 — asked the 210 §1 way; still failing, the picker says « La liste
     n'a pas pu être chargée » with « Réessayer » — never « No subfolders
     here — files save into this folder. » when the folders were never read. */
  const { entry: folderRead, again: folderAgain, ask: askFolders } = useReadMark(PAGE_LISTS.attachmentFolders);
  const folderLists = usePageListContext(folderRead);
  useEffect(() => {
    let cancelled = false;
    void askFolders("the destination's folders", () =>
      api<{ folders?: { name: string }[] } | null>(`${base}?path=${encodeURIComponent(pathStr)}${rootQ}`).then((d) => d?.folders),
    ).then((rows) => {
      if (cancelled) return;
      setRead({ key: pathKey, folders: (rows ?? []) as { name: string }[] });
    });
    return () => {
      cancelled = true;
    };
    // 213 §11.4 — keyed on the JOINED path: the same folder asked again only when it changes (or « Réessayer »)
  }, [base, pathStr, pathKey, rootQ, folderAgain, askFolders]);
  const folderLoading = read.key !== pathKey;
  const folders = folderLoading ? [] : read.folders;
  return (
    <>
          {/* Phase 21.1 (#4) — destination subfolder picker. */}
          <div
            style={{
              border: "1px solid var(--br)",
              borderRadius: RAD.r6,
              padding: "6px 8px",
              margin: "0 0 10px",
              background: "var(--bd)",
            }}
          >
            {roots && onRoot && (
              <div data-file-roots="" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4, marginBottom: 6, fontSize: FS.s10_5 }}>
                <button
                  type="button"
                  data-file-root={MATTER_ROOT}
                  aria-pressed={root === MATTER_ROOT}
                  disabled={!roots.matter.hasFolder}
                  onClick={() => {
                    chooseRoot(MATTER_ROOT);
                  }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 7px", border: "1px solid var(--br)", borderRadius: RAD.r5, background: root === MATTER_ROOT ? "var(--as)" : "var(--bc)", color: root === MATTER_ROOT ? "var(--ac)" : "var(--tp)", cursor: roots.matter.hasFolder ? "pointer" : "default", opacity: roots.matter.hasFolder ? 1 : 0.6 }}
                >
                  <Folder size={11} />
                  {t("fileWhere.matter_folder", { fallback: "Matter folder" })}
                </button>
                {searchClients && clients.length > 5 && (
                  <input autoComplete="off" /* 207 §18 */
                    className="field-input no-contact-autofill"
                    type="text"
                    value={clientQ}
                    placeholder={t("fileWhere.search_placeholder", { fallback: "Search a client…" })}
                    aria-label={t("fileWhere.search_placeholder", { fallback: "Search a client…" })}
                    onChange={(e) => {
                      setClientQ(e.target.value);
                    }}
                    style={{ flex: "1 1 160px", fontSize: FS.s10_5, padding: "2px 6px" }}
                  />
                )}
                {shownClients.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-file-root={clientRoot(c.id)}
                    aria-pressed={root === clientRoot(c.id)}
                    disabled={!c.hasFolder}
                    title={c.hasFolder ? t("fileWhere.client_folder", { fallback: "Client folder" }) : t("clientFile.no_folder", { fallback: "no folder recorded" })}
                    onClick={() => {
                      chooseRoot(clientRoot(c.id));
                    }}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 7px", border: "1px solid var(--br)", borderRadius: RAD.r5, background: root === clientRoot(c.id) ? "var(--as)" : "var(--bc)", color: clientColor(c), cursor: c.hasFolder ? "pointer" : "default" }}
                  >
                    <Folder size={11} />
                    {c.name}
                    {!c.hasFolder && <span style={{ fontStyle: "italic" }}>— {t("clientFile.no_folder", { fallback: "no folder recorded" })}</span>}
                  </button>
                ))}
              </div>
            )}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 2,
                fontSize: FS.s11,
                marginBottom: folders.length || folderLoading ? 6 : 0,
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setPath([]);
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: path.length ? "var(--ac)" : "var(--tp)",
                  cursor: "pointer",
                  fontWeight: 600,
                  padding: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                }}
                title={t("attSave.crumb_root_title", {
                  fallback: "Case root folder",
                })}
              >
                <Folder size={12} />
                {rootName}
              </button>
              {path.map((seg, i) => (
                <span
                  key={i}
                  style={{ display: "inline-flex", alignItems: "center", gap: 2 }}
                >
                  <ChevronRight size={11} style={{ color: "var(--tm)" }} />
                  <button
                    type="button"
                    onClick={() => {
                      setPath(path.slice(0, i + 1));
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color:
                        i === path.length - 1 ? "var(--tp)" : "var(--ac)",
                      cursor: "pointer",
                      fontWeight: 600,
                      padding: 0,
                    }}
                  >
                    {seg}
                  </button>
                </span>
              ))}
              {/* 206E addendum §A7 — beside the breadcrumb: a new folder in the one shown */}
              {canCreateFolder && (
                <NewFolderField
                  t={t}
                  base={base}
                  root={root}
                  path={path}
                  onDone={([name]) => {
                    setPath([...path, name]);
                  }}
                />
              )}
            </div>
            <ListLoadContext.Provider value={folderLists}>
            <ListNotLoaded lists={[PAGE_LISTS.attachmentFolders]} variant="inline">
            {folderLoading && (
              <p style={{ fontSize: FS.s10_5, color: "var(--tm)", margin: 0 }}>
                {t("attSave.folders_loading", { fallback: "Loading folders…" })}
              </p>
            )}
            {!folderLoading && folders.length === 0 && (
              <p style={{ fontSize: FS.s10_5, color: "var(--tm)", margin: 0 }}>
                {t("attSave.no_subfolders", {
                  fallback: "No subfolders here — files save into this folder.",
                })}
              </p>
            )}
            {!folderLoading && folders.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {folders.map((f) => (
                  <button
                    key={f.name}
                    type="button"
                    onClick={() => {
                      setPath([...path, f.name]);
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: FS.s10_5,
                      padding: "2px 7px",
                      border: "1px solid var(--br)",
                      borderRadius: RAD.r5,
                      background: "var(--bc)",
                      color: "var(--tp)",
                      cursor: "pointer",
                    }}
                    title={t("attSave.open_subfolder", {
                      name: f.name,
                      fallback: `Open ${f.name}`,
                    })}
                  >
                    <Folder size={11} />
                    {f.name}
                  </button>
                ))}
              </div>
            )}
            </ListNotLoaded>
            </ListLoadContext.Provider>
          </div>
    </>
  );
}
